import type {RouteLocationRaw} from 'vue-router';
import {LOGIN_FLOW_QUERY_PARAM} from '~/utils/loginHelper';

export const pageNames = {
	index: 'INDEX',
	login: 'LOGIN',
	logout: 'LOGOUT',
	user: 'USER',
	userEdit: 'USER_EDIT',
	applicationUsersOverview: 'APPLICATION_USERS_OVERVIEW',
	applicationUsersEdit: 'APPLICATION_USERS_EDIT',
	applicationUsersInvite: 'APPLICATION_USERS_INVITE',
	tocDeclined: 'TOC_DECLINED',
	help: 'HELP',
	privacyStatement: 'PRIVACY_STATEMENT',
	inventarisation: 'INVENTARISATION',
	topicStatusOverview: 'TOPIC_STATUS_OVERVIEW',
	topicDetail: 'TOPIC_DETAIL',
	questionDetail: 'QUESTION_DETAIL',
	questionsEnd: 'QUESTIONS_END',
	riskOverview: 'RISK_OVERVIEW',
	riskDetail: 'RISK_DETAIL',
	riskEdit: 'RISK_EDIT',
	actionOverview: 'ACTION_OVERVIEW',
	actionDetail: 'ACTION_DETAIL',
	actionDetailEdit: 'ACTION_DETAIL_EDIT',
	reportOverview: 'REPORT_OVERVIEW',
	reportCreate: 'REPORT_CREATE',
	companiesOverview: 'COMPANIES_OVERVIEW',
	companyNew: 'COMPANY_NEW',
	companyDashboard: 'COMPANY_DASHBOARD',
	companyDetail: 'COMPANY_DETAIL',
	companyDetailEdit: 'COMPANY_DETAIL_EDIT',
	companyUsers: 'COMPANY_USERS',
	companyUsersEdit: 'COMPANY_USERS_EDIT',
	companyUsersInvite: 'COMPANY_USERS_INVITE',
	rieOverview: 'RIE_OVERVIEW',
	rieDashboard: 'RIE_DASHBOARD',
	rieDetail: 'RIE_DETAIL',
	rieEdit: 'RIE_EDIT',
	rieNew: 'RIE_NEW',
	manageCatalog: 'MANAGE_CATALOG',
	responsiblesOverview: 'RESPONSIBLES_OVERVIEW',
	responsibleDetail: 'RESPONSIBLE_DETAIL',
	requestLink: 'REQUEST_LINK',
	stigasQuestionnaireOverview: 'STIGAS_QUESTIONNAIRE_OVERVIEW',
	stigasQuestionnairePublic: 'STIGAS_QUESTIONNAIRE_PUBLIC',
	stigasAccidentsOverview: 'STIGAS_ACCIDENTS_OVERVIEW',
	stigasAccidentNew: 'STIGAS_ACCIDENT_NEW',
	stigasAccidentEdit: 'STIGAS_ACCIDENT_EDIT',
	stigasAccidentDetail: 'STIGAS_ACCIDENT_DETAIL',
	stigasAccidentsBackgroundInfo: 'STIGAS_ACCIDENTS_BACKGROUND_INFO'
} as const;

export const companiesOverviewLink = (): RouteLocationRaw => {
	return {
		name: pageNames.companiesOverview
	};
};

export const questionDetailLink = (topicKey: string, questionKey: string, rieId?: string): RouteLocationRaw => {
	return {
		name: pageNames.questionDetail,
		params: {
			topicKey,
			questionKey,
			rieId
		}
	};
};

/**
 * Link to page which is shown after the last question in a topic.
 */
export const questionsEndLink = (topicKey: string, rieId?: string): RouteLocationRaw => {
	return {
		name: pageNames.questionsEnd,
		params: {
			topicKey,
			rieId
		}
	};
};

export const inventarisationLink = (companyId: string, rieId: string): RouteLocationRaw => {
	return {
		name: pageNames.inventarisation,
		params: {
			companyId,
			rieId
		}
	};
};

export const topicOverviewLink = (topicKey: string): RouteLocationRaw => {
	return {
		name: pageNames.topicDetail,
		params: {
			topicKey
		}
	};
};

export const riskDetailLink = (riskId: number): RouteLocationRaw => {
	return {
		name: pageNames.riskDetail,
		params: {
			riskId
		}
	};
};

export const riskEditLink = (riskId: number): RouteLocationRaw => {
	return {
		name: pageNames.riskEdit,
		params: {
			riskId
		}
	};
};

export const riskOverviewLink = (rieId: string): RouteLocationRaw => {
	return {
		name: pageNames.riskOverview,
		params: {
			rieId
		}
	};
};

export const actionDetailLink = (actionId: number, rieId?: string, companyId?: string): RouteLocationRaw => {
	return {
		name: pageNames.actionDetail,
		params: {
			actionId,
			rieId,
			companyId
		}
	};
};

export const actionDetailEditLink = (actionId: number): RouteLocationRaw => {
	return {
		name: pageNames.actionDetailEdit,
		params: {
			actionId
		}
	};
};

export const responsibleDetailLink = (responsibleId: number): RouteLocationRaw => {
	return {
		name: pageNames.responsibleDetail,
		params: {
			id: responsibleId
		}
	};
};

export const rieDashboardLink = (companyId: number | string, rieId: number): RouteLocationRaw => {
	return {
		name: pageNames.rieDashboard,
		params: {
			companyId,
			rieId
		}
	};
};
export const createReport = (companyId: number | string, rieId: number): RouteLocationRaw => {
	return {
		name: pageNames.reportCreate,
		params: {
			companyId,
			rieId
		}
	};
};

export const rieDetailLink = (companyId: number | string, rieId: number | string): RouteLocationRaw => {
	return {
		name: pageNames.rieDetail,
		params: {
			companyId,
			rieId
		}
	};
};

export const rieOverviewLink = (companyId: number | string, loginflow = false): RouteLocationRaw => {
	const query = loginflow ? {[LOGIN_FLOW_QUERY_PARAM]: null} : undefined;

	return {
		name: pageNames.rieOverview,
		params: {
			companyId
		},
		query
	};
};

export const rieEditLink = (companyId: number, rieId: number | string): RouteLocationRaw => {
	return {
		name: pageNames.rieEdit,
		params: {
			companyId,
			rieId
		}
	};
};

export const companyRieOverview = (companyId: number | string): RouteLocationRaw => {
	return {
		name: pageNames.companyDashboard,
		params: {
			companyId
		}
	};
};

export const inviteUserLink = (companyId: number | string): RouteLocationRaw => {
	return {
		name: pageNames.companyUsersInvite,
		params: {
			companyId
		}
	};
};

export const editUserLink = (companyId: number, userId: string): RouteLocationRaw => {
	return {
		name: pageNames.companyUsersEdit,
		params: {
			companyId,
			userId
		}
	};
};

export const editApplicationUsersLink = (userId: string): RouteLocationRaw => {
	return {
		name: pageNames.applicationUsersEdit,
		params: {
			userId
		}
	};
};

export const inviteApplicationUsersLink = (): RouteLocationRaw => {
	return {
		name: pageNames.applicationUsersInvite
	};
};

